<template>
  <div
    class="c-stream-settings-group"
    :class="componentClass"
  >
    <BaseInputWrapper new-design>
      <template #control>
        <fieldset
          class="row tw:border-0 tw:p-0"
          data-testid="group-selection-fieldset"
        >
          <legend class="field-heading q-mb-sm">
            {{ $t("stream_settings_group.group") }}
          </legend>
          <RadioInput
            v-model="handlePlaceIntoNewGroup"
            class="col c-stream-settings-group__radio"
            :color="inputColor"
            data-testid="group-selection-existing"
            :disabled="disabled"
            :label="existingGroupLabel"
            :name="groupSelectionName"
            new-design
            :value="false"
          />
          <RadioInput
            v-model="handlePlaceIntoNewGroup"
            class="col c-stream-settings-group__radio"
            :color="inputColor"
            data-testid="group-selection-new"
            :disabled="disabled"
            :label="$t('stream_settings_group.new_group')"
            :name="groupSelectionName"
            new-design
            :value="newGroup"
          />
        </fieldset>
      </template>
    </BaseInputWrapper>

    <TeamSelector
      v-if="isAdminPage && !placeIntoNewGroup"
      v-model="handleNewGroupTeamId"
      exclude-all-option
      :validations="['notBlank']"
    />

    <StreamGroupLabel
      v-if="placeIntoNewGroup"
      v-model:color="handleNewGroupColour"
      v-model:label="handleNewGroupLabel"
      v-model:organisation-team-ids="handleOrganisationTeamIds"
      data-testid="stream-group-label"
      :disabled="disabled"
      :hide-color-picker-label="bookmarkMentions"
      :placeholder="labelPlaceholder"
      :show-organisation-teams-selector="showOrganisationTeamsSelector"
      :spacing="spacing"
    />
    <BaseInputSelect
      v-else
      v-model="handlePlaceIntoExistingGroup"
      :disabled="disabled"
      emit-value
      :label="$t('stream_settings_group.group_label')"
      new-design
      option-label="label"
      option-prop="id"
      :options="streamGroups"
      required
      :validations="mandatoryFieldsMissing.group ? ['notBlank'] : []"
    />
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { useId } from "vue";

import { BaseInputSelect, BaseInputWrapper } from "shared/components/base";
import RadioInput from "shared/components/core/forms/RadioInput.vue";
import TeamSelector from "shared/components/selectors/TeamSelector.vue";
import StreamGroupLabel from "shared/components/streams/StreamGroup/StreamGroupLabel.vue";
import { isSocialStream } from "shared/helpers/stream";
import { useStreamGroupsStore } from "shared/stores/streamGroups";
import { useUserStore } from "shared/stores/user";

export default {
  name: "StreamSettingsGroup",
  components: {
    BaseInputSelect,
    BaseInputWrapper,
    RadioInput,
    StreamGroupLabel,
    TeamSelector,
  },
  props: {
    groupId: {
      type: Number,
      default: null,
    },
    stream: {
      type: Object,
      default: null,
    },
    placeIntoNewGroup: {
      type: Boolean,
    },
    newGroupLabel: {
      type: String,
      default: "",
    },
    bookmarkGroup: {
      type: Boolean,
    },
    newGroupColor: {
      type: String,
      default: "silver",
    },
    mandatoryFieldsMissing: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
    },
    organisationStreamGroups: {
      type: Array,
      default: () => [],
    },
    bookmarkMentions: {
      type: Boolean,
    },
    spacing: {
      type: String,
      default: "xl",
    },
    newGroupTeamId: {
      type: [String, Number],
      default: null,
    },
    organisationTeamIds: {
      type: Array,
      default: () => [],
    },
    showOrganisationTeamsSelector: {
      type: Boolean,
    },
  },
  emits: [
    "update:newGroupColor",
    "update:newGroupLabel",
    "update:placeIntoNewGroup",
    "update:groupId",
    "update:organisationTeamIds",
    "update:newGroupTeamId",
  ],
  setup() {
    const streamGroupsStore = useStreamGroupsStore();

    const {
      traditionalStreamGroupsWithoutLock: mentionStreamGroups,
      socialOwnedStreamGroupsWithoutLocked: socialStreamGroups,
      traditionalBookmarkGroupsWithoutLocked: traditionalBookmarkGroups,
      socialBookmarkGroupsWithoutLocked: socialBookmarkGroups,
    } = storeToRefs(streamGroupsStore);

    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);
    const groupSelectionName = useId();

    return {
      mentionStreamGroups,
      socialStreamGroups,
      currentUser,
      groupSelectionName,
      traditionalBookmarkGroups,
      socialBookmarkGroups,
    };
  },
  data() {
    return {
      newGroup: true,
    };
  },
  computed: {
    isAdminPage() {
      return (
        ["mention-streams", "social-streams"].includes(this.$route.name) &&
        this.$features.has("has_team_workspaces")
      );
    },
    streamGroups() {
      if (this.isAdminPage) return this.organisationStreamGroups;

      if (this.bookmarkGroup) {
        const bookmarkGroups =
          this.isSocialBookmarkStream || this.isSocialStream
            ? this.socialBookmarkGroups
            : this.traditionalBookmarkGroups;

        return bookmarkGroups.filter(
          (group) => group.user_id === this.currentUser.id
        );
      }

      const groups = this.isSocialStream
        ? this.socialStreamGroups
        : this.mentionStreamGroups;

      return groups.filter((group) => group.user_id === this.currentUser.id);
    },
    handleNewGroupTeamId: {
      get() {
        return this.newGroupTeamId;
      },
      set(val) {
        this.$emit("update:newGroupTeamId", val);
      },
    },
    handlePlaceIntoExistingGroup: {
      get() {
        return this.groupId;
      },
      set(val) {
        this.$emit("update:groupId", val);
      },
    },
    handleNewGroupColour: {
      get() {
        return this.newGroupColor;
      },
      set(val) {
        this.$emit("update:newGroupColor", val);
      },
    },
    handleNewGroupLabel: {
      get() {
        return this.newGroupLabel;
      },
      set(val) {
        this.$emit("update:newGroupLabel", val);
      },
    },
    handlePlaceIntoNewGroup: {
      get() {
        return this.placeIntoNewGroup;
      },
      set(val) {
        this.$emit("update:placeIntoNewGroup", val);
      },
    },
    handleOrganisationTeamIds: {
      get() {
        return this.organisationTeamIds;
      },
      set(val) {
        this.$emit("update:organisationTeamIds", val);
      },
    },
    existingGroupLabel() {
      return this.stream?.id && !this.bookmarkMentions
        ? this.$t("stream_settings_group.move_to_existing_group")
        : this.$t("stream_settings_group.existing_group");
    },
    isSocialStream() {
      return isSocialStream(this.stream);
    },
    isSocialBookmarkStream() {
      return this.stream?.social_bookmarks_stream;
    },
    inputColor() {
      return this.isSocialStream ? "ds-purple-5" : "ds-denim-9";
    },
    componentClass() {
      return `c-stream-settings-group--spacing-${this.spacing}`;
    },
    labelPlaceholder() {
      return this.stream?.keywords || "";
    },
  },
};
</script>

<style lang="scss">
.c-stream-settings-group {
  --spacing: var(--s-spacing-lg);

  gap: var(--spacing);
  display: grid;

  &--spacing-xl {
    --spacing: var(--s-spacing-xl);
  }

  &__radio {
    margin: 0 !important;
  }
}
</style>
